import React, { useEffect, useState } from 'react';
import AeroSitePreview from '../../../components/Aero/AeroSitePreview';
import { readPreview, requestPreviewRefresh } from '../../../utils/previewSnapshots';
import { relativeTimeInline } from '../sitesUtils';

/**
 * The preview on a site's page (HTML and Figma sites): the stored snapshot first, the live site on
 * request.
 *
 * The snapshot is a screenshot of the real page at desktop size, the same picture the Sites list
 * shows. It opens instantly and has the shape a visitor sees; the live iframe is a short, wide box,
 * loads the whole app and Figma every time, and can look different. So the snapshot is the default,
 * "Try it live" swaps in the iframe, and a small line says how fresh the picture is. Without a
 * snapshot (new sites, password-protected sites) the live site shows, as before.
 *
 *   site          the url doc (previewUrl, previewColors, previewAt, previewStatus)
 *   host          the address shown in the window bar
 *   liveUrl       what the live iframe opens
 *   iframeKey     changes when the live site changes, so the iframe reloads
 *   fetchSite     reloads the site doc (used while a new snapshot is on its way)
 */
const POLL_MS = 5000;
const POLL_TIMES = 12;

export default function SitePreviewHero({ site, host, liveUrl, iframeKey, fetchSite }) {
  const [live, setLive] = useState(false);
  const [askedFrom, setAskedFrom] = useState(null); // previewAt (ms) when we asked for a refresh
  const [gaveUp, setGaveUp] = useState(false);
  const [notice, setNotice] = useState('');

  const preview = readPreview(site);
  const atMs = preview && preview.at ? preview.at.getTime() : 0;
  const updating = !gaveUp && (site.previewStatus === 'queued' || askedFrom !== null);
  const showShot = !!preview && !live;

  // a new picture arrived
  useEffect(() => {
    if (askedFrom !== null && atMs !== askedFrom) setAskedFrom(null);
  }, [atMs, askedFrom]);

  // while a new picture is on its way, look again every few seconds (for about a minute)
  useEffect(() => {
    if (!updating || !fetchSite) return undefined;
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      fetchSite();
      if (n >= POLL_TIMES) {
        clearInterval(id);
        setGaveUp(true);
        setAskedFrom(null);
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [updating, fetchSite]);

  const refreshNow = async () => {
    setNotice('');
    setGaveUp(false);
    try {
      const r = await requestPreviewRefresh(site.id, site.shared ? site.ownerUid : undefined);
      if (r.status === 'queued') setAskedFrom(atMs);
      else if (r.status === 'recent') setNotice('Refreshed a moment ago. Try again in a minute.');
      else if (r.status === 'skipped') {
        setNotice(r.reason === 'password' ? "No preview is taken while a password is on." : 'No preview for this site yet.');
      } else if (r.status === 'disabled') setNotice('Previews are switched off.');
    } catch (e) {
      setNotice("Couldn't refresh the preview. Try again in a moment.");
    }
  };

  let text;
  if (notice) text = notice;
  else if (live) text = 'Showing the live site.';
  else if (updating) text = 'Updating preview…';
  else if (preview && preview.at) text = `Preview updated ${relativeTimeInline(preview.at)}`;
  else if (!preview) text = 'No preview picture yet. Showing the live site.';
  else text = 'Preview';

  return (
    <div className="aero-sites-hero-wrap">
      <AeroSitePreview
        host={host}
        previewUrl={showShot ? preview.url : null}
        previewColors={showShot ? preview.colors : null}
        color={!showShot && preview && preview.colors[0] ? preview.colors[0] : null}
        className="aero-sites-hero"
      >
        <iframe key={iframeKey} src={liveUrl} title={`${site.generatedUrl} live preview`} loading="lazy" />
      </AeroSitePreview>
      <div className="aero-sites-previewbar">
        <span className="aero-sites-previewbar-text" role="status">{text}</span>
        <span className="aero-sites-previewbar-actions">
          {preview && (
            <button type="button" className="aero-btn aero-btn-secondary aero-btn-sm" onClick={() => setLive((v) => !v)}>
              {live ? 'Show preview' : 'Try it live'}
            </button>
          )}
          {!live && (
            <button type="button" className="aero-btn aero-btn-secondary aero-btn-sm" onClick={refreshNow} disabled={updating}>
              Refresh preview
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
